import { AnimatePresence, motion, useScroll, useTransform } from "framer-motion";
import { useEffect, useState } from "react";
import { BadgeCheck } from "lucide-react";
import { HERO, SITE } from "../data/content";

const studentPhotos = [
    { src: "/assets/prestasi-santri/abid-naufal-fadzilla.webp", alt: "Santri Abid Naufal Fadzilla" },
    { src: "/assets/prestasi-santri/ashbar-azinuddin.webp", alt: "Santri Ashbar Azinuddin" },
    { src: "/assets/prestasi-santri/diajeng-husna-nikmatus-sholikhah.webp", alt: "Santri Diajeng Husna Nikmatus Sholikhah" },
    { src: "/assets/prestasi-santri/muhammad-bilal-asyifa.webp", alt: "Santri Muhammad Bilal Asyifa" },
    { src: "/assets/prestasi-santri/muhammad-fikri-anshorullah.webp", alt: "Santri Muhammad Fikri Anshorullah" },
    { src: "/assets/prestasi-santri/salman.webp", alt: "Santri Salman" },
    { src: "/assets/prestasi-santri/syifa-nur-aulia.webp", alt: "Santri Syifa Nur Aulia" },
];

const lineWrap = {
    hidden: {},
    show: (i) => ({
        transition: { delayChildren: 0.25 + i * 0.16, staggerChildren: 0.05 },
    }),
};

const lineInner = {
    hidden: { y: "115%" },
    show: {
        y: "0%",
        transition: { duration: 0.95, ease: [0.22, 1, 0.36, 1] },
    },
};

const MaskedLine = ({ index, children }) => (
    <motion.span
        className="block overflow-hidden pb-1"
        variants={lineWrap}
        custom={index}
        initial="hidden"
        animate="show"
    >
        <motion.span className="block" variants={lineInner}>
            {children}
        </motion.span>
    </motion.span>
);

export const Hero = () => {
    const [activePhoto, setActivePhoto] = useState(0);
    const { scrollY } = useScroll();
    const yVisual = useTransform(scrollY, [0, 700], [0, 70]);
    const yBadge = useTransform(scrollY, [0, 700], [0, -40]);

    useEffect(() => {
        const interval = window.setInterval(() => {
            setActivePhoto((current) => (current + 1) % studentPhotos.length);
        }, 4000);

        return () => window.clearInterval(interval);
    }, []);

    return (
        <section
            id="beranda"
            data-testid="hero-section"
            className="relative overflow-hidden bg-bh-offwhite pt-[132px] pb-20 sm:pt-[152px] sm:pb-24"
        >
            <div aria-hidden="true" className="absolute inset-0 pattern-star opacity-[0.045]" />
            <div
                aria-hidden="true"
                className="absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full bg-bh-light blur-3xl opacity-70"
            />

            <div className="relative max-w-[1565px] mx-auto px-6 sm:px-8 grid lg:grid-cols-12 gap-14 lg:gap-10 items-center">
                <div className="lg:col-span-7">
                    <motion.p
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, ease: "easeOut" }}
                        className="inline-flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.28em] text-bh-gold mb-7"
                    >
                        <svg
                            aria-hidden="true"
                            viewBox="0 0 24 24"
                            className="w-4 h-4 shrink-0 text-bh-gold"
                            fill="currentColor"
                        >
                            <path d="M12 0l2.6 9.4L24 12l-9.4 2.6L12 24l-2.6-9.4L0 12l9.4-2.6z" />
                        </svg>
                        {HERO.eyebrow}
                    </motion.p>

                    <h1
                        data-testid="hero-headline"
                        aria-label={HERO.headline}
                        className="font-serif font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.12] tracking-tight text-bh-deep"
                    >
                        <MaskedLine index={0}>Mencetak Generasi</MaskedLine>
                        <MaskedLine index={1}>
                            <em className="italic text-bh-green">Qurani</em>
                        </MaskedLine>
                        <MaskedLine index={2}>Pemimpin Peradaban</MaskedLine>
                    </h1>

                    <motion.p
                        data-testid="hero-subheadline"
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.85, ease: "easeOut" }}
                        className="mt-7 max-w-xl text-base sm:text-lg leading-relaxed text-bh-gray"
                    >
                        {HERO.subheadline}
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 1.0, ease: "easeOut" }}
                        className="mt-9 flex flex-wrap items-center gap-4"
                    >
                        <a
                            href="#daftar"
                            data-testid="hero-cta-daftar"
                            className="inline-flex items-center justify-center rounded-full bg-bh-green px-8 py-4 text-sm font-bold uppercase tracking-[0.12em] text-white shadow-[0_14px_34px_rgba(16,134,108,0.28)] transition-all duration-300 hover:bg-bh-dark hover:shadow-[0_18px_40px_rgba(6,76,61,0.32)] hover:-translate-y-0.5"
                        >
                            {HERO.primaryCta}
                        </a>
                        <a
                            href="#program"
                            data-testid="hero-cta-program"
                            className="inline-flex items-center justify-center rounded-full border-2 border-bh-green/30 px-8 py-[14px] text-sm font-bold uppercase tracking-[0.12em] text-bh-dark transition-colors duration-300 hover:border-bh-gold hover:text-bh-green"
                        >
                            {HERO.secondaryCta}
                        </a>
                    </motion.div>

                    <motion.ul
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.8, delay: 1.2 }}
                        className="mt-10 flex flex-wrap gap-3"
                        aria-label="Sorotan utama"
                    >
                        {HERO.pills.map((pill) => (
                            <li
                                key={pill}
                                className="rounded-full border border-bh-green/15 bg-white px-4 py-2 text-xs font-semibold tracking-wide text-bh-dark"
                            >
                                {pill}
                            </li>
                        ))}
                    </motion.ul>
                </div>

                <div className="lg:col-span-5 relative">
                    <motion.div
                        style={{ y: yVisual }}
                        initial={{ opacity: 0, y: 40 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
                        className="relative"
                    >
                        <div
                            aria-hidden="true"
                            className="absolute -inset-3 rounded-2xl border border-bh-gold/40"
                        />
                        <div
                            data-testid="hero-student-carousel"
                            role="img"
                            aria-label="Foto prestasi santri PPTQ Baitul Haq"
                            className="relative aspect-square overflow-hidden rounded-xl bg-bh-light shadow-[0_24px_60px_rgba(6,76,61,0.16)]"
                        >
                            <AnimatePresence mode="wait">
                                <motion.img
                                    key={studentPhotos[activePhoto].src}
                                    src={studentPhotos[activePhoto].src}
                                    alt={studentPhotos[activePhoto].alt}
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    transition={{ duration: 0.6 }}
                                    className="absolute inset-0 h-full w-full object-cover"
                                />
                            </AnimatePresence>
                        </div>
                    </motion.div>
                    <motion.div
                        style={{ y: yBadge }}
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.7, delay: 1.1, ease: "easeOut" }}
                        className="absolute -bottom-8 lg:bottom-[75px] -left-4 sm:-left-8 bg-white rounded-2xl border border-bh-green/10 shadow-[0_18px_44px_rgba(6,76,61,0.14)] px-5 py-4 flex items-center gap-3"
                    >
                        <span className="flex items-center justify-center w-10 h-10 rounded-full bg-bh-light">
                            <BadgeCheck className="w-5 h-5 text-bh-green" aria-hidden="true" />
                        </span>
                        <span className="leading-tight">
                            <span className="block text-xs text-bh-gray">NSP Terdaftar</span>
                            <span data-testid="hero-nsp" className="block text-sm font-bold text-bh-deep">
                                {SITE.nsp}
                            </span>
                        </span>
                    </motion.div>
                </div>
            </div>
        </section>
    );
};
